import React, { useEffect, useState } from "react";
import { Truck, Package, CheckCircle2, MapPin, ExternalLink, Copy, XCircle, FileText, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

const TRACK_URLS = {
  JNE: "https://jne.co.id/en/tracking-package",
  SPX: "https://spx.co.id/id",
  SHOPEE: "https://spx.co.id/id",
  JNT: "https://www.jet.co.id/track",
};
const resiTrackUrl = (courier, no) => {
  const c = (courier || "").toUpperCase();
  if (c === "SPX" || c === "SHOPEE") return "https://spx.co.id/id";
  return `https://cekresi.com/?noresi=${encodeURIComponent(no || "")}`;
};

const STEPS = [
  { id: "processing", label: "Diproses", icon: Package },
  { id: "shipped", label: "Dikirim", icon: Truck },
  { id: "delivered", label: "Diterima", icon: CheckCircle2 },
];
const stepIndex = (s) => STEPS.findIndex((x) => x.id === s);

export default function MemberTracking({ requests, demo }) {
  const [resi, setResi] = useState([]);
  useEffect(() => {
    if (demo) return;
    api.get("/ecommerce/resi").then((r) => setResi(r.data)).catch(() => {});
  }, [demo]);

  const approved = (requests || []).filter((r) => r.status === "approved");
  const isEmpty = approved.length === 0 && resi.length === 0;

  return (
    <div className="space-y-6" data-testid="tracking-list">
      {resi.length > 0 && (
        <div className="space-y-4" data-testid="shopee-resi-list">
          <h3 className="font-display text-xl text-showty-navy flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-[#ee4d2d]" /> Resi Shopee</h3>
          {resi.map((r) => <ShopeeResiCard key={r.id} r={r} />)}
        </div>
      )}

      {approved.length > 0 && (
        <div className="space-y-4">
          {resi.length > 0 && <h3 className="font-display text-xl text-showty-navy flex items-center gap-2"><Truck className="w-5 h-5 text-showty-red" /> Pengiriman Campaign</h3>}
          {approved.map((r) => {
            const current = stepIndex(r.tracking_status || "processing");
            return (
              <div key={r.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`tracking-${r.id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-showty-pink/30 shrink-0">
                    {r.product_image && <img src={r.product_image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-showty-navy truncate">{r.product_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {r.courier ? <span className="font-semibold text-showty-navy uppercase">{r.courier}</span> : "Kurir belum ditentukan"}
                      {r.tracking_number ? <span> · Resi: <span className="font-mono">{r.tracking_number}</span></span> : ""}
                    </p>
                  </div>
                </div>
                <div className="mt-5 flex items-center" data-testid={`tracking-progress-${r.id}`}>
                  {STEPS.map((step, i) => {
                    const done = i <= current;
                    const Icon = step.icon;
                    return (
                      <React.Fragment key={step.id}>
                        <div className="flex flex-col items-center">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 ${done ? "bg-showty-red border-showty-red text-white" : "bg-white border-border text-muted-foreground"}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <span className={`mt-1.5 text-[11px] ${done ? "text-showty-navy font-medium" : "text-muted-foreground"}`}>{step.label}</span>
                        </div>
                        {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i < current ? "bg-showty-red" : "bg-border"}`} />}
                      </React.Fragment>
                    );
                  })}
                </div>
                {r.tracking_number && (
                  <div className="mt-4 flex flex-wrap items-center gap-2" data-testid={`track-actions-${r.id}`}>
                    <a href={TRACK_URLS[(r.courier || "").toUpperCase()] || "#"} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs rounded-full bg-showty-red text-white px-3 py-1.5 hover:bg-[#9e1f24]" data-testid={`track-link-${r.id}`}>
                      <ExternalLink className="w-3 h-3" /> Lacak di {r.courier || "Kurir"}
                    </a>
                    <button onClick={() => { navigator.clipboard?.writeText(r.tracking_number); toast.success("Resi disalin"); }}
                      className="inline-flex items-center gap-1 text-xs rounded-full border border-border px-3 py-1.5 text-showty-navy hover:bg-showty-pink/30" data-testid={`track-copy-${r.id}`}>
                      <Copy className="w-3 h-3" /> Salin Resi {r.tracking_number}
                    </button>
                  </div>
                )}
                {r.tracking_updated_at && (
                  <p className="mt-4 text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Update terakhir: {new Date(r.tracking_updated_at).toLocaleString("id-ID")}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isEmpty && (
        <div className="rounded-2xl border border-dashed border-border bg-white/50 py-16 text-center text-sm text-muted-foreground" data-testid="tracking-empty">
          <Truck className="w-6 h-6 mx-auto mb-2 text-showty-red/50" /> Belum ada pengiriman. Resi Shopee & sample yang di-approve akan muncul di sini.
        </div>
      )}
    </div>
  );
}

const ShopeeResiCard = ({ r }) => {
  const cancelled = r.status === "cancelled";
  const current = stepIndex(r.status);
  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid={`shopee-resi-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-[#ee4d2d]/10 text-[#ee4d2d]">SHOPEE · {r.courier || "SPX"}</span>
            {r.order_no && <span className="text-xs text-muted-foreground">Order: <span className="font-medium text-showty-navy">{r.order_no}</span></span>}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">No Resi: <span className="font-mono text-showty-navy">{r.tracking_number}</span></p>
        </div>
        <ResiFileThumb id={r.id} />
      </div>

      {cancelled ? (
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-red-100 text-red-700 px-3 py-1.5 text-xs font-medium" data-testid={`resi-cancelled-${r.id}`}>
          <XCircle className="w-4 h-4" /> Pesanan Dibatalkan
        </div>
      ) : (
        <div className="mt-5 flex items-center" data-testid={`resi-progress-${r.id}`}>
          {STEPS.map((step, i) => {
            const done = i <= current;
            const Icon = step.icon;
            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 ${done ? "bg-[#ee4d2d] border-[#ee4d2d] text-white" : "bg-white border-border text-muted-foreground"}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className={`mt-1.5 text-[11px] ${done ? "text-showty-navy font-medium" : "text-muted-foreground"}`}>{step.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i < current ? "bg-[#ee4d2d]" : "bg-border"}`} />}
              </React.Fragment>
            );
          })}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid={`resi-actions-${r.id}`}>
        <a href={`${API}/ecommerce/resi/${r.id}/file`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs rounded-full border border-border px-3 py-1.5 text-showty-navy hover:bg-showty-pink/30" data-testid={`resi-view-file-${r.id}`}>
          <FileText className="w-3 h-3" /> Lihat File Resi
        </a>
        <a href={resiTrackUrl(r.courier, r.tracking_number)} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs rounded-full bg-[#ee4d2d] text-white px-3 py-1.5 hover:bg-[#d63e1f]" data-testid={`resi-track-${r.id}`}>
          <ExternalLink className="w-3 h-3" /> Lacak Paket
        </a>
        <button onClick={() => { navigator.clipboard?.writeText(r.tracking_number); toast.success("Resi disalin"); }}
          className="inline-flex items-center gap-1 text-xs rounded-full border border-border px-3 py-1.5 text-showty-navy hover:bg-showty-pink/30" data-testid={`resi-copy-${r.id}`}>
          <Copy className="w-3 h-3" /> Salin Resi
        </button>
      </div>
      {r.note && <p className="mt-3 text-xs text-muted-foreground italic">Catatan: {r.note}</p>}
    </div>
  );
};

const ResiFileThumb = ({ id }) => {
  const [ok, setOk] = useState(true);
  const src = `${API}/ecommerce/resi/${id}/file`;
  if (!ok) return null;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="shrink-0" data-testid={`resi-thumb-${id}`}>
      <img src={src} alt="Resi" onError={() => setOk(false)} className="w-16 h-16 rounded-lg object-cover border border-border bg-showty-pink/20" />
    </a>
  );
};
